// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0
import { useEffect, useState } from 'react';
import { shouldExcludeResource, getResourceName, parseCommaSeparatedValues, VIEW_PRESET_TYPES } from './ResourceFilteringUtils';
import { DEFAULT_COSTS_INTERVAL } from '../config/constants';

function validateCanvas(canvas, functionName, defaultReturn = null) {
    if (!canvas || canvas.destroyed()) {
        console.warn(`DiagramFilteringUtils.${functionName}: Canvas is ${!canvas ? 'null' : 'destroyed'}`);
        return { isValid: false, defaultReturn };
    }
    return { isValid: true, defaultReturn: null };
}

function normalizeFilterState(filterState) {
    if (!filterState) {
        return { viewMode: 'all', excludePrefixes: '', keywordFilter: '', hideAutoGenerated: false };
    }
    return {
        viewMode: ['all', 'default', 'advanced'].includes(filterState.viewMode) ? filterState.viewMode : 'all',
        excludePrefixes: typeof filterState.excludePrefixes === 'string' ? filterState.excludePrefixes : '',
        keywordFilter: typeof filterState.keywordFilter === 'string' ? filterState.keywordFilter : '',
        hideAutoGenerated: typeof filterState.hideAutoGenerated === 'boolean' ? filterState.hideAutoGenerated : false
    };
}

function isVisible(node) {
    return node && node.style('display') !== 'none';
}

function matchesFilter(resourceName, filterValue, shouldMatch = true) {
    if (!filterValue?.trim()) return shouldMatch;
    
    const values = parseCommaSeparatedValues(filterValue).map(v => v.toLowerCase());
    const nameLower = resourceName.toLowerCase();
    const hasMatch = values.some(val => nameLower.includes(val));
    
    return shouldMatch ? hasMatch : !hasMatch;
}

export function shouldHideNode(node, filterState) {
    if (!node || node.data('type') !== 'resource') return false;
    
    const { viewMode, excludePrefixes, keywordFilter, hideAutoGenerated } = normalizeFilterState(filterState);
    // Fix: Pass node properties directly to getResourceName, not wrapped in another properties object
    const nodeProperties = node.data('properties');
    const resourceName = getResourceName({ properties: nodeProperties });
    
    if (viewMode === 'all') return false;
    if (viewMode === 'default') return shouldExcludeResource(resourceName);
    
    // Advanced mode - only apply custom filters if they have values
    if (hideAutoGenerated && shouldExcludeResource(resourceName)) return true;
    
    // Only apply exclude filter if it has a value
    if (excludePrefixes && excludePrefixes.trim() && !matchesFilter(resourceName, excludePrefixes, false)) return true;
    
    // Only apply include (keyword) filter if it has a value  
    if (keywordFilter && keywordFilter.trim() && !matchesFilter(resourceName, keywordFilter, true)) return true;
    
    return false;
}

export function applyFiltersToCanvas(canvas, filterState) {
    const defaultStats = { hiddenNodes: 0, hiddenEdges: 0, hiddenParents: 0 };
    const validation = validateCanvas(canvas, 'applyFiltersToCanvas', defaultStats);
    if (!validation.isValid) return validation.defaultReturn;
    
    const allNodes = canvas.nodes();
    if (!allNodes?.length) return defaultStats;
    
    const stats = { ...defaultStats };
    
    canvas.batch(() => {
        canvas.elements().style('display', 'element');
        
        // Hide filtered nodes and their edges
        canvas.nodes()
            .filter(n => n.data('type') === 'resource')
            .forEach(node => {
                if (shouldHideNode(node, filterState)) {
                    node.style('display', 'none');
                    node.connectedEdges().style('display', 'none');
                    stats.hiddenNodes++;
                }
            });
        
        stats.hiddenEdges = canvas.edges().filter(e => !isVisible(e)).length;
        
        // Hide empty parent nodes (deepest first)
        const parents = canvas.nodes().filter(n => n.isParent());
        const parentsWithDepth = [];
        
        parents.forEach(parent => {
            let depth = 0, current = parent.parent();
            while (current?.length) {
                depth++;
                current = current.parent();
            }
            parentsWithDepth.push({ parent, depth });
        });
        
        parentsWithDepth
            .sort((a, b) => b.depth - a.depth)
            .forEach(({ parent }) => {
                const children = parent.children();
                if (children.length && children.every(c => !isVisible(c))) {
                    parent.style('display', 'none');
                    stats.hiddenParents++;
                } else {
                    parent.style('display', 'element');
                }
            });
    });
    
    return stats;
}

export function getVisibleElements(canvas) {
    const defaultReturn = { nodes: [], edges: [] };
    const validation = validateCanvas(canvas, 'getVisibleElements', defaultReturn);
    if (!validation.isValid) return validation.defaultReturn;
    
    return {
        nodes: canvas.nodes().filter(isVisible).jsons(),
        edges: canvas.edges().filter(isVisible).jsons()
    };
}

export function clearAllFilters(canvas) {
    const validation = validateCanvas(canvas, 'clearAllFilters');
    if (!validation.isValid) return;
    
    canvas.batch(() => canvas.elements().style('display', 'element'));
}

export function getVisibleDescendants(node) {
    if (!node?.isParent()) return [node];
    return node.descendants().filter(isVisible);
}

export function getVisibleNodesFromSelection(nodeCollection) {
    if (!nodeCollection?.length) return [];
    
    const visibleNodes = [];
    const seenIds = new Set();
    
    nodeCollection.forEach(node => {
        const nodes = node.isParent() ? getVisibleDescendants(node) : (isVisible(node) ? [node] : []);
        nodes.forEach(n => {
            const id = n.id();
            if (!seenIds.has(id)) {
                seenIds.add(id);
                visibleNodes.push(n);
            }
        });
    });
    
    return visibleNodes;
}

export function useDebounce(value, delay) {
    const [debouncedValue, setDebouncedValue] = useState(value);

    useEffect(() => {
        const handler = setTimeout(() => setDebouncedValue(value), delay);
        return () => clearTimeout(handler);
    }, [value, delay]);

    return debouncedValue;
}

export function createDefaultDiagramSettings(dataSettings = null) {
    const defaults = {
        costInterval: DEFAULT_COSTS_INTERVAL,
        accounts: [],
        regions: [],
        resourceTypes: [],
        hideSelected: true,
        hideEdges: false,
        viewPreset: VIEW_PRESET_TYPES.FULL,
    };
    
    return dataSettings 
        ? { 
            ...defaults,
            ...dataSettings, 
            costInterval: dataSettings.costInterval ?? DEFAULT_COSTS_INTERVAL,
            viewPreset: dataSettings.viewPreset ?? defaults.viewPreset
          }
        : defaults;
}

export function applyDiagramSettingsFilters(canvas, settings, updateCanvas, updateResources, removeResource) {
    const validation = validateCanvas(canvas, 'applyDiagramSettingsFilters', false);
    if (!validation.isValid || !settings) return false;

    const currentResources = canvas.nodes().filter(e => e?.data('type') === 'resource');
    const props = { accounts: 'accountId', regions: 'awsRegion', resourceTypes: 'resourceType' };
    const activeFilters = Object.keys(props).filter(prop => settings[prop]?.length > 0);

    if (activeFilters.length > 0) {
        const matchesAllFilters = r => activeFilters.every(prop => 
            (settings[prop] ?? []).includes(r.data('properties')?.[props[prop]])
        );

        const toRemove = (settings?.hideSelected ?? true)
            ? currentResources.filter(r => r.data('properties') && matchesAllFilters(r))
            : currentResources.filter(r => r.data('properties') && !matchesAllFilters(r));

        if (toRemove.length > 0) {
            removeResource(canvas, updateCanvas, updateResources, toRemove.map(i => i.id()));
        }
    }

    canvas.batch(() => {
        canvas.edges().style('visibility', (settings?.hideEdges ?? false) ? 'hidden' : 'visible');
    });

    return activeFilters.length > 0;
}

export function showAllCanvasElements(canvas) {
    const validation = validateCanvas(canvas, 'showAllCanvasElements', false);
    if (!validation.isValid) return false;

    canvas.batch(() => canvas.elements().style('display', 'element'));
    return true;
}
